// WeekNavigation.js mit Logo und Logout
import React, { useState, useEffect } from 'react';
import { getWeekDates, getWeekNumber, formatDateDE } from '../utils/dateUtils';
import CerpaxLogo from './CerpaxLogo';
import EntryHelpButton from './EntryHelpButton';
import { API_BASE_URL } from '../config/apiConfig';

const WeekNavigation = ({ selectedWeek = new Date(), onWeekChange, user, onLogout }) => {
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [lastCheck, setLastCheck] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const handlePasswordChange = async () => {
    setPwError('');
    setPwSuccess('');

    if (!pwForm.oldPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      setPwError('Bitte alle Felder ausfüllen');
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwError('Neue Passwörter stimmen nicht überein');
      return;
    }
    if (pwForm.newPassword.length < 4) {
      setPwError('Neues Passwort muss mindestens 4 Zeichen haben');
      return;
    }

    setPwLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tenant/mitarbeiter/${user.mitid}/passwort`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Key': user.tenant || user.database
        },
        body: JSON.stringify({
          oldPassword: pwForm.oldPassword,
          newPassword: pwForm.newPassword
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setPwError(data.message || 'Fehler beim Ändern');
      } else {
        setPwSuccess('Passwort erfolgreich geändert');
        setPwForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setShowPasswordModal(false), 1500);
      }
    } catch (err) {
      setPwError('Verbindungsfehler');
    } finally {
      setPwLoading(false);
    }
  };

  // Sicherstellen, dass selectedWeek ein gültiges Datum ist
  const currentDate = selectedWeek instanceof Date && !isNaN(selectedWeek) ? selectedWeek : new Date();
  const { startDate, endDate } = getWeekDates(currentDate);
  const weekNumber = getWeekNumber(currentDate);

  // Verbindungsstatus überprüfen
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/health`);
        if (response.ok) {
          setConnectionStatus('connected');
        } else {
          setConnectionStatus('disconnected');
        }
      } catch (error) {
        setConnectionStatus('disconnected');
      }
      setLastCheck(new Date());
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000); // Alle 30 Sekunden prüfen
    return () => clearInterval(interval);
  }, []);

  const handlePreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    onWeekChange(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    onWeekChange(newDate);
  };

  const handleCurrentWeek = () => {
    onWeekChange(new Date());
  };

  return (
    <div className="header-container">
      <div className="top-header">
        {/* Verbindungsstatus + Hilfe */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="vpn-status">
            <div className={`status-indicator ${connectionStatus}`}>
              {connectionStatus === 'connected' ? 'Online' : 'Offline'}
            </div>
            {lastCheck && (
              <div className="last-check">
                {lastCheck.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false
                })}
              </div>
            )}
          </div>
          <EntryHelpButton
            buttonLabel="?"
            buttonClassName="btn btn-info"
            buttonStyle={{
              borderRadius: '50%',
              width: '35px',
              height: '35px',
              padding: '0',
              fontSize: '18px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            tenant={user?.tenant || user?.database}
            user={user}
          />
        </div>

        {/* Logo */}
        <div className="logo-container">
          <CerpaxLogo />
        </div>

        {/* Einstellungen + Abmelden */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="btn btn-info"
              style={{
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Einstellungen"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
            </button>
            {showSettings && (
              <>
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                  onClick={() => setShowSettings(false)}
                />
                <div style={{
                  position: 'absolute', top: '42px', right: 0, background: 'white',
                  borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  padding: '8px 0', minWidth: '200px', zIndex: 1000
                }}>
                  {user?.username && (
                    <div style={{ padding: '8px 16px', color: '#666', fontSize: '13px', borderBottom: '1px solid #eee' }}>
                      Angemeldet als <strong>{user.username}</strong>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setShowSettings(false);
                      setPwError(''); setPwSuccess('');
                      setPwForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                      setShowPasswordModal(true);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                      padding: '10px 16px', background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: '14px', textAlign: 'left',
                      color: '#333'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    Passwort ändern
                  </button>
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button onClick={onLogout} className="btn-logout">
              Abmelden
            </button>
            {user?.username && (
              <span style={{ fontSize: '0.9rem', color: '#999', marginTop: '2px' }}>{user.username}</span>
            )}
          </div>
        </div>
      </div>

      {/* Passwort-Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '400px', width: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Passwort ändern</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Altes Passwort:</label>
                <input
                  type="password"
                  value={pwForm.oldPassword}
                  onChange={e => setPwForm({ ...pwForm, oldPassword: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box' }}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Neues Passwort:</label>
                <input
                  type="password"
                  value={pwForm.newPassword}
                  onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Neues Passwort bestätigen:</label>
                <input
                  type="password"
                  value={pwForm.confirmPassword}
                  onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') handlePasswordChange(); }}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>
              {pwError && <div style={{ color: '#dc2626', fontSize: '14px' }}>{pwError}</div>}
              {pwSuccess && <div style={{ color: '#15803d', fontSize: '14px' }}>{pwSuccess}</div>}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  onClick={handlePasswordChange}
                  disabled={pwLoading}
                  style={{
                    flex: 1, padding: '12px', fontSize: '16px', background: '#3b82f6', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer'
                  }}
                >
                  {pwLoading ? 'Speichern...' : 'Speichern'}
                </button>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  style={{
                    flex: 1, padding: '12px', fontSize: '16px', background: '#6b7280', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer'
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wochennavigation */}
      <div className="week-navigation-container">
        <div className="week-navigation">
          <button onClick={handlePreviousWeek} className="nav-button prev-week">
            &larr;
          </button>
          
          <button onClick={handleCurrentWeek} className="nav-button current-week-btn">
            Aktuelle Woche
          </button>
          
          <button onClick={handleNextWeek} className="nav-button next-week">
            &rarr;
          </button>
        </div>
        
        <div className="week-info">
          <span className="week-label">KW {weekNumber} | {formatDateDE(startDate)} - {formatDateDE(endDate)}</span>
        </div>
      </div>
    </div>
  );
};

export default WeekNavigation;